import React, { PropsWithChildren, useContext, useReducer } from "react";
import { WidgetContextReducer } from "./reducer";
import { getWidgetStateFromStorage } from "./storage";
import { WidgetContainerProps, WidgetContextState, WidgetContextActions } from "./widgettypes";

const WidgetContext = React.createContext<{
  widgets: WidgetContainerProps[];
}>({
  widgets: [],
});
const WidgetContextDispatch = React.createContext<any>({});

export const WidgetContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const initialState = getWidgetStateFromStorage();

  const [state, stateDispatch] = useReducer<
    React.Reducer<WidgetContextState, WidgetContextActions>
  >(WidgetContextReducer, initialState);

  const { widgets } = state;

  const memoizedWidgets = React.useMemo<WidgetContainerProps[]>(
    () =>
      Object.values(widgets).sort(
        (a: any, b: any) => a.position.index - b.position.index
      ),
    [widgets]
  );

  const memoizedReturn = React.useMemo(
    () => ({
      widgets: memoizedWidgets,
    }),
    [memoizedWidgets]
  );

  return (
    <WidgetContext.Provider value={memoizedReturn}>
      <WidgetContextDispatch.Provider value={stateDispatch}>
        {children}
      </WidgetContextDispatch.Provider>
    </WidgetContext.Provider>
  );
};

export const useWidgetContext = () => {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error(
      "useWidgetContext must be used within a WidgetContextProvider"
    );
  }
  return context;
};

export const useWidgets = () => {
  const { widgets } = useWidgetContext();
  return widgets;
};

export const useWidgetContextDispatch = () => {
  const context = useContext(WidgetContextDispatch);
  if (!context) {
    throw new Error(
      "useWidgetContextDispatch must be used within a WidgetContextProvider"
    );
  }
  return context;
};

export * from "./widgettypes";
